import { CheckCircledIcon, DividerHorizontalIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Box, Code, Flex, IconButton, Inset, Popover, ScrollArea, Table, Text } from '@workos-inc/design-system';
import { AccessibleIcon } from 'radix-ui';
import * as React from 'react';
export interface PropDefinition {
  name: string;
  required?: boolean | string | React.ReactElement;
  default?: string | boolean;
  type?: string;
  typeSimple: SimplePropType
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});
  description?: string | React.ReactNode;
}
export const PropsTable = ({
  data,
  propHeaderFixedWidth = true
}: {
  data: PropDefinition[];
  propHeaderFixedWidth?: boolean;
}) => <Box asChild my="5" data-sentry-element="Box" data-sentry-component="PropsTable" data-sentry-source-file="props-table.tsx">
    <Table.Root data-sentry-element="Table.Root" data-sentry-source-file="props-table.tsx">
      <Table.Content data-sentry-element="Table.Content" data-sentry-source-file="props-table.tsx">
        <Table.Header data-sentry-element="Table.Header" data-sentry-source-file="props-table.tsx">
          <Table.Row data-sentry-element="Table.Row" data-sentry-source-file="props-table.tsx">
            <Table.ColumnHeader style={{
            width: propHeaderFixedWidth ? '37%' : 'auto'
          }} data-sentry-element="Table.ColumnHeader" data-sentry-source-file="props-table.tsx">
              Prop
            </Table.ColumnHeader>
            <Table.ColumnHeader data-sentry-element="Table.ColumnHeader" data-sentry-source-file="props-table.tsx">Type</Table.ColumnHeader>
            <Table.ColumnHeader data-sentry-element="Table.ColumnHeader" data-sentry-source-file="props-table.tsx">Default</Table.ColumnHeader>
            <Table.ColumnHeader justify="center" width="96px" data-sentry-element="Table.ColumnHeader" data-sentry-source-file="props-table.tsx">
              Required
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body data-sentry-element="Table.Body" data-sentry-source-file="props-table.tsx">
          {data.map(({
          name,
          type,
          typeSimple,
          required,
          default: defaultValue,
          description
        }, i) => {
          return <Table.Row key={`${name}-${i}`} style={{
            whiteSpace: 'nowrap'
          }}>
                  <Table.RowHeader>
                    <Flex align="center" display="inline-flex" gap="2">
                      <Box>
                        <Code size="2">{name}</Code>
                      </Box>
                      {description && <Popover.Root>
                          <Popover.Trigger>
                            <IconButton color="gray" size="1">
                              <AccessibleIcon.Root label="Prop description">
                                <InfoCircledIcon />
                              </AccessibleIcon.Root>
                            </IconButton>
                          </Popover.Trigger>
                          <Popover.Content align="center" className="radix-themes-custom-fonts" side="top" style={{
                    maxWidth: 350
                  }} onOpenAutoFocus={event => {
                    event.preventDefault();
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    (event.currentTarget as HTMLElement)?.focus();
                  }}>
                            <Text as="div" size="2">
                              {description}
                            </Text>
                          </Popover.Content>
                        </Popover.Root>}
                    </Flex>
                  </Table.RowHeader>
                  <Table.Cell>
                    <Flex align="center" display="inline-flex" gap="2">
                      <Box>
                        <Code color="gray" size="2">
                          {typeSimple}
                        </Code>
                      </Box>
                      {!!type && <Popover.Root>
                          <Popover.Trigger>
                            <IconButton color="gray" size="1">
                              <AccessibleIcon.Root label="See full type">
                                <InfoCircledIcon />
                              </AccessibleIcon.Root>
                            </IconButton>
                          </Popover.Trigger>
                          <Popover.Content align="center" side="top" style={{
                    maxWidth: 'min(1240px, 95vw)'
                  }}>
                            <Inset>
                              <ScrollArea.Root type="auto">
                                <ScrollArea.Viewport>
                                  <Box style={{
                            paddingTop: 'var(--inset-padding-top)',
                            paddingRight: 'var(--inset-padding-right)',
                            paddingBottom: 'var(--inset-padding-bottom)',
                            paddingLeft: 'var(--inset-padding-left)'
                          }}>
                                    <Code color="gray" size="2" variant="ghost" style={{
                              whiteSpace: 'pre',
                              display: 'block'
                            }}>
                                      {type}
                                    </Code>
                                  </Box>
                                </ScrollArea.Viewport>
                                <ScrollArea.Scrollbar orientation="vertical" />
                                <ScrollArea.Scrollbar orientation="horizontal" />
                              </ScrollArea.Root>
                            </Inset>
                          </Popover.Content>
                        </Popover.Root>}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    {defaultValue ? <Code color="gray" size="2">
                        {defaultValue}
                      </Code> : <AccessibleIcon.Root label="No default value">
                        <DividerHorizontalIcon style={{
                  color: 'var(--gray-8)'
                }} />
                      </AccessibleIcon.Root>}
                  </Table.Cell>

                  <Table.Cell justify="center">
                    {(() => {
                if (!required) {
                  return <AccessibleIcon.Root label="Not required">
                            <Flex asChild display="inline-flex">
                              <DividerHorizontalIcon style={{
                        color: 'var(--gray-8)'
                      }} />
                            </Flex>
                          </AccessibleIcon.Root>;
                }
                if (required === true) {
                  return <AccessibleIcon.Root label="Required">
                            <Flex asChild display="inline-flex">
                              <CheckCircledIcon color="green" />
                            </Flex>
                          </AccessibleIcon.Root>;
                }
                return <Popover.Root>
                          <Popover.Trigger>
                            <IconButton color="gray" size="1">
                              <AccessibleIcon.Root label="Conditionally required">
                                <InfoCircledIcon />
                              </AccessibleIcon.Root>
                            </IconButton>
                          </Popover.Trigger>
                          <Popover.Content align="center" className="radix-themes-custom-fonts" side="top" style={{
                    maxWidth: 350
                  }} onOpenAutoFocus={event => {
                    event.preventDefault();
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    (event.currentTarget as HTMLElement)?.focus();
                  }}>
                            <Text as="div" size="2">
                              {required}
                            </Text>
                          </Popover.Content>
                        </Popover.Root>;
              })()}
                  </Table.Cell>
                </Table.Row>;
        })}
        </Table.Body>
      </Table.Content>
    </Table.Root>
  </Box>;
type SimplePropType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'function' | 'null' | 'undefined' | 'enum';